import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { filesApi } from '../api/files.api';
import { useParams } from 'react-router-dom';

export const useFileActions = () => {
  const queryClient = useQueryClient();
  const { id } = useParams();

  const invalidateFile = (id: string) => {
    queryClient.invalidateQueries({ queryKey: ['resultData', id] });
    queryClient.invalidateQueries({ queryKey: ['downloadData', id] });
    queryClient.invalidateQueries({ queryKey: ['fontData', id] });
    queryClient.invalidateQueries({ queryKey: ['mrzData', id] });
    queryClient.invalidateQueries({ queryKey: ['notesData', id] });
    queryClient.invalidateQueries({ queryKey: ['compareData', id] });
  };

  const followMutation = useMutation({
    mutationFn: filesApi.followFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      if (id) {
        invalidateFile(id);
      }
      toast.success('Le fichier a été ajouté aux favoris');
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: filesApi.unfollowFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      if (id) {
        invalidateFile(id);
      }
      toast.success('Le fichier a été retiré des favoris');
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: filesApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const markAsUnreadMutation = useMutation({
    mutationFn: filesApi.markAsUnread,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: filesApi.archiveFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      if (id) {
        invalidateFile(id);
      }
      toast.success('Le fichier a été archivé');
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: filesApi.unarchiveFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      toast.success('Le fichier a été désarchivé');
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const markAsReadMultiMutation = useMutation({
    mutationFn: filesApi.markAsReadMulti,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const markAsUnreadMultiMutation = useMutation({
    mutationFn: filesApi.markAsUnreadMulti,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const archiveMultiMutation = useMutation({
    mutationFn: filesApi.archiveFileMulti,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      toast.success('Les fichiers ont été archivés');
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const unarchiveMultiMutation = useMutation({
    mutationFn: filesApi.unarchiveFileMulti,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      toast.success('Les fichiers ont été désarchivés');
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: filesApi.deleteFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      toast.success('Le fichier a été supprimé');
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const deleteMultiMutation = useMutation({
    mutationFn: filesApi.deleteFileMulti,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      toast.success('Les fichiers ont été supprimés');
    },
    onError: () => {
      toast.error('Une erreur est survenue');
    },
  });

  const addNotesMutation = useMutation({
    mutationFn: filesApi.addNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      if (id) {
        invalidateFile(id);
      }
      toast.success('Vos notes ont été enregistrées');
    },
    onError: () => {
      toast.error('Une erreur est survenue, veuillez réessayer');
    },
  });

  const toggleFavorite = async (fileId: string, isFavorite: boolean) => {
    if (isFavorite) {
      await unfollowMutation.mutateAsync(fileId);
    } else {
      await followMutation.mutateAsync(fileId);
    }
  };

  const toggleRead = async (fileId: string, isRead: boolean) => {
    if (isRead) {
      await markAsUnreadMutation.mutateAsync(fileId);
    } else {
      await markAsReadMutation.mutateAsync(fileId);
    }
  };

  const toggleArchive = async (fileId: string, isArchived: boolean) => {
    if (isArchived) {
      await unarchiveMutation.mutateAsync(fileId);
    } else {
      await archiveMutation.mutateAsync(fileId);
    }
  };

  const toggleReadMulti = async (fileIds: string[], allRead: boolean) => {
    if (allRead) {
      await markAsUnreadMultiMutation.mutateAsync(fileIds);
    } else {
      await markAsReadMultiMutation.mutateAsync(fileIds);
    }
  };

  const toggleArchiveMulti = async (
    fileIds: string[],
    allArchived: boolean
  ) => {
    if (allArchived) {
      await unarchiveMultiMutation.mutateAsync(fileIds);
    } else {
      await archiveMultiMutation.mutateAsync(fileIds);
    }
  };

  const deleteFile = async (fileId: string) => {
    await deleteMutation.mutateAsync(fileId);
  };

  const deleteMulti = async (fileIds: string[]) => {
    await deleteMultiMutation.mutateAsync(fileIds);
  };

  const addNotes = async (fileId: string, comment: string, fraud: boolean) => {
    await addNotesMutation.mutateAsync({ fileId, comment, fraud });
  };

  return {
    toggleFavorite,
    toggleRead,
    toggleArchive,
    toggleReadMulti,
    toggleArchiveMulti,
    deleteFile,
    deleteMulti,
    addNotes,
    isLoading:
      followMutation.isPending ||
      unfollowMutation.isPending ||
      markAsReadMutation.isPending ||
      markAsUnreadMutation.isPending ||
      archiveMutation.isPending ||
      unarchiveMutation.isPending ||
      markAsReadMultiMutation.isPending ||
      markAsUnreadMultiMutation.isPending ||
      archiveMultiMutation.isPending ||
      unarchiveMultiMutation.isPending ||
      deleteMutation.isPending ||
      deleteMultiMutation.isPending ||
      addNotesMutation.isPending,
  };
};
